import React, { useEffect, useId, useRef, useState } from 'react';
import Tag from 'Components/DesignSystem/Tag/Tag';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { FieldHelper } from 'Components/DesignSystem/fieldHelper';
import { CHIP_MAX_LENGTH, chipKey } from '../manifest/valueUtils';

interface ChipsInputProps {
  testId: string;
  value: string[];
  onChange: (next: string[]) => void;
  extendable?: boolean;
  /** Floor the list can't drop below — a chip is only removable while the list is above it. */
  min?: number;
  /** Cap on the list, extendable or not — the add-input is hidden once it holds this many
   * chips, and an `n/max` counter is shown whenever it is set. */
  max?: number;
  /** Row-level error, e.g. a stored list already over `max`. */
  error?: string | null;
  disabled?: boolean;
}

/** Case- and whitespace-insensitive match. A stored item is not type-checked on load, so a
 * legacy non-string (e.g. `null`) must never reach `.toLowerCase()`. */
const sameChip = (chip: unknown, text: string) => typeof chip === 'string' && chipKey(chip) === chipKey(text);

/** Repeatable string chips, with an optional add-input when `extendable`. */
const ChipsInput: React.FC<ChipsInputProps> = ({
  testId,
  value,
  onChange,
  extendable,
  min = 0,
  max,
  error = null,
  disabled,
}) => {
  const [draft, setDraft] = useState('');
  // The text Enter was refused for. Derived against the live list, so the warning goes the
  // moment the chip it matched is removed — not only when the draft is edited.
  const [refused, setRefused] = useState<string | null>(null);
  const duplicate = refused !== null && value.some((chip) => sameChip(chip, refused));
  const atMax = max !== undefined && value.length >= max;
  const overMax = max !== undefined && value.length > max;
  const countRef = useRef<HTMLSpanElement>(null);
  // Set when an add fills the list: the add-input the user is typing in unmounts, so focus
  // is handed to the counter rather than dropped to <body>.
  const focusCountAfterAdd = useRef(false);
  const errorId = `${useId()}-error`;

  // Runs after EVERY commit, so the flag lives exactly one render: the one the add caused
  // (`setDraft('')` always re-renders). If the parent did not apply the add, the list is not
  // at max then, and the flag must not linger to steal focus on some later, unrelated fill.
  useEffect(() => {
    if (!focusCountAfterAdd.current) return;
    focusCountAfterAdd.current = false;
    if (atMax) countRef.current?.focus();
  });

  const addChip = () => {
    const trimmed = draft.trim();
    if (!trimmed || atMax) return;
    // The same suggestion twice wastes a capped slot and shows the shopper a repeat.
    if (value.some((chip) => sameChip(chip, trimmed))) {
      setRefused(trimmed);
      return;
    }
    if (max !== undefined && value.length + 1 >= max) focusCountAfterAdd.current = true;
    onChange([...value, trimmed]);
    setDraft('');
  };

  // A non-extendable list is a fixed set — no add, no remove. An extendable list is
  // removable down to its `min` floor (e.g. "at least one placeholder"), never below it.
  // Any list over its `max` is removable, fixed or not: it blocks Save until it is back
  // under the cap, so the merchant must have a way to get it there.
  const canRemove = overMax || (extendable && value.length > min);

  return (
    <div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2" data-testid={testId}>
        {value.map((chip, index) => (
          <Tag
            key={index}
            label={typeof chip === 'string' ? chip : ''}
            disabled={disabled}
            onRemove={canRemove ? () => onChange(value.filter((_, i) => i !== index)) : undefined}
            data-testid={`${testId}-chip-${index}`}
          />
        ))}
        {extendable && !atMax && (
          <TextField
            label=""
            placeholder="Add & press Enter"
            value={draft}
            onChange={(next: string) => {
              setDraft(next);
              setRefused(null);
            }}
            onKeyDown={(e) => {
              // The Enter that confirms an IME candidate (Japanese, Chinese …) is not a commit;
              // Safari reports it only through keyCode 229, not `isComposing`.
              if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.keyCode === 229) return;
              e.preventDefault();
              addChip();
            }}
            errorMessage={duplicate ? 'Already added.' : undefined}
            // The backend rejects a longer chip; stop the typing rather than fail the Save.
            maxLength={CHIP_MAX_LENGTH}
            disabled={disabled}
            className="tw-w-56"
            data-testid={`${testId}-add-input`}
          />
        )}
        {max !== undefined && (
          <span
            ref={countRef}
            tabIndex={-1}
            aria-live="polite"
            aria-describedby={error ? errorId : undefined}
            className={error || overMax ? 'tw-text-xs tw-text-tone-danger-ink' : 'tw-text-xs tw-text-ink-tertiary'}
            data-testid={`${testId}-count`}
          >
            {/* "4/4" alone is what a screen reader heard when focus landed here — say what it counts. */}
            <span aria-hidden="true">
              {value.length}/{max}
            </span>
            <span className="tw-sr-only" data-testid={`${testId}-count-label`}>
              {`${value.length} of ${max} added${atMax ? ' — limit reached' : ''}`}
            </span>
          </span>
        )}
      </div>
      {error && <FieldHelper id={errorId} text={error} error className="tw-mt-1" data-testid={`${testId}-error`} />}
    </div>
  );
};

export default ChipsInput;
